import React, {useEffect, useState} from 'react';
import styles from './Table.module.css';
import FilterSection, {Filter} from './FilterSection';
import TableSection, {Column} from './TableSection';
import PaginationSection from './PaginationSection';

// 테이블 컴포넌트의 props 타입 정의
export interface TableProps<T> {
    columns: Column<T>[];                   // 테이블 컬럼 정의
    data: T[];                             // 표시할 데이터 배열
    filters?: Filter[];                     // 필터 옵션 배열 (선택적)
    showPagination?: boolean;               // 페이지네이션 표시 여부
    page?: number;                          // 현재 페이지
    pageSize?: number;                      // 페이지당 항목 수
    totalItems?: number;                    // 전체 항목 수
    onPageChange?: (page: number) => void;  // 페이지 변경 핸들러
    onFilterChange?: (key: string, value: any) => void;  // 필터 변경 핸들러
    onSortChange?: (field: string | null, direction: 'asc' | 'desc' | null) => void;  // 정렬 변경 핸들러
    loading?: boolean;                      // 로딩 상태
    className?: string;                     // 추가 CSS 클래스
    size?: 'small' | 'medium' | 'large';    // 테이블 크기
    headerTabs?: {                          // 헤더 탭 설정 (선택적)
        label: string;
        active: boolean;
        onClick: () => void;
    }[];
    onRowClick?: (row: T) => void;         // 행 클릭 핸들러
    onReset?: () => void;                  // 필터 전체 초기화
}

// 정렬 상태 인터페이스
interface SortState {
    field: string | null;       // 정렬 기준 필드
    direction: 'asc' | 'desc' | null;  // 정렬 방향
}

// 테이블 컴포넌트
// 제네릭 타입 T는 Record<string, any>를 확장하여 문자열 키를 가진 객체만 허용
function Table<T extends Record<string, any>>({
                                                  columns,
                                                  data,
                                                  filters = [],
                                                  showPagination = true,
                                                  page = 1,
                                                  pageSize = 10,
                                                  totalItems = 0,
                                                  onPageChange,
                                                  onFilterChange,
                                                  onSortChange,
                                                  loading = false,
                                                  className = '',
                                                  size = 'medium',
                                                  headerTabs,
                                                  onRowClick,
                                                  onReset
                                              }: TableProps<T>) {
    // 필터 값들을 관리하는 상태
    // 초기값은 filters 배열을 기반으로 생성
    const [filterValues, setFilterValues] = useState<Record<string, any>>(() => {
        return filters.reduce((acc, filter) => ({
            ...acc,
            [filter.key]: filter.value ?? (filter.multiple ? [] : '')
        }), {});
    });

    // 정렬 상태 관리
    const [sort, setSort] = useState<SortState>({
        field: null,
        direction: null
    });

    // 현재 열려있는 필터의 키를 관리하는 상태
    const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

    // 외부 filters prop이 변경될 때 로컬 filterValues 동기화
    useEffect(() => {
        const newFilterValues = filters.reduce((acc, filter) => ({
            ...acc,
            [filter.key]: filter.value ?? (filter.multiple ? [] : '')
        }), {});
        setFilterValues(newFilterValues);
    }, [filters]);

    // 필터 영역 외 클릭 시 필터 닫기 이벤트 핸들러
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openFilterKey &&
                !(event.target as Element).closest(`.${styles.filterGroup}`) &&
                !(event.target as Element).closest(`.${styles.dateRangeDropdown}`)) {
                setOpenFilterKey(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [openFilterKey]);

    // 필터 토글 핸들러
    const handleToggleFilter = (key: string | null) => {
        setOpenFilterKey(prevKey => {
            return prevKey === key ? null : key;
        });
    };

    // 정렬 변경 핸들러
    const handleSort = (field: string | null, direction: 'asc' | 'desc' | null) => {
        const newSort: SortState = {
            field,
            direction
        };

        setSort(newSort);
        onSortChange?.(field, direction);
    };

    // 필터 값 변경 핸들러
    const handleFilterChange = (key: string, value: any) => {
        const newFilters = {...filterValues, [key]: value};
        setFilterValues(newFilters);
        onFilterChange?.(key, value);
    };

    // 필터 초기화 핸들러
    const handleReset = () => {
        // 상위 컴포넌트의 초기화 함수 먼저 호출
        onReset?.();

        // 그 다음 로컬 상태를 초기 값으로 재설정
        setTimeout(() => {
            const initialFilterValues = filters.reduce((acc, filter) => ({
                ...acc,
                [filter.key]: filter.value ?? (filter.multiple ? [] : '')
            }), {});
            setFilterValues(initialFilterValues);
        }, 0);

        // 열린 필터 닫기
        setOpenFilterKey(null);
    };

    // 전체 페이지 수 계산
    const totalPages = Math.ceil(totalItems / pageSize);

    return (
        <div className={`${styles.container} ${className} ${styles[size]}`}>
            {/* 헤더 탭 영역 */}
            {headerTabs && headerTabs.length > 0 && (
                <div className={styles.tabContainer}>
                    {headerTabs.map((tab, index) => (
                        <button
                            key={index}
                            className={`${styles.tab} ${tab.active ? styles.activeTab : ''}`}
                            onClick={tab.onClick}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            {/* 필터 섹션 */}
            {filters.length > 0 && (
                <FilterSection
                    filters={filters.map(filter => ({
                        ...filter,
                        isOpen: openFilterKey === filter.key,
                        onToggle: () => handleToggleFilter(filter.key)
                    }))}
                    filterValues={filterValues}
                    onFilterChange={handleFilterChange}
                    onReset={onReset ? handleReset : undefined}
                />
            )}

            {/* 테이블 섹션 */}
            <TableSection<T>
                columns={columns}
                data={data}
                loading={loading}
                sort={sort}
                onSort={handleSort}
                onRowClick={onRowClick}
            />

            {/* 페이지네이션 섹션 */}
            {showPagination && totalPages > 1 && (
                <PaginationSection
                    page={page}
                    totalPages={totalPages}
                    onPageChange={onPageChange!}
                />
            )}
        </div>
    );
}

export default Table;